import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { DepartmentPeriodData } from '../../services/api';

interface DepartmentDonutProps {
  departments: DepartmentPeriodData[];
}

const DEPT_COLORS: Record<string, string> = {
  'Membership Sales': '#8b5cf6',
  'Membership L4': '#a78bfa',
  'Membership L5': '#c4b5fd',
  'SP 30': '#7c3aed',
  'Club Sales (Callaway)': '#3b82f6',
  'School Sales': '#f59e0b',
  'Product Sales': '#22c55e',
  'Trade-In Sales': '#06b6d4',
  'Front-End Sales': '#ec4899',
};

const formatCurrency = (value: number) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

export const DepartmentDonut: React.FC<DepartmentDonutProps> = ({ departments }) => {
  const data = departments
    .filter(d => d.netRevenue > 0)
    .map(d => ({ name: d.name, value: d.netRevenue }));

  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900 mb-2">Revenue Mix</h3>
      <div className="h-64 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="45%"
              innerRadius={55}
              outerRadius={85}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={DEPT_COLORS[entry.name] || '#6b7280'} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [`$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, 'Revenue']}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value: string) => <span className="text-gray-600">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Center label */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ marginBottom: 40 }}>
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{formatCurrency(total)}</div>
            <div className="text-xs text-gray-400">Net Revenue</div>
          </div>
        </div>
      </div>
    </div>
  );
};
